<template>
    <div class="w-full">
        <div v-if="updatableSelector"
             class="mb-3 w-full bg-gray-100 dark:bg-gray-700 border border-gray-400 text-gray-700 dark:text-gray-200 px-4 py-3 mt-4 rounded relative"
             role="alert">
            <strong class="font-bold">Shift+Click to edit details</strong>
        </div>

        <div class="mb-3" v-if="interactive">
            <input
                type="text"
                class="block bg-white dark:bg-gray-700 text-black dark:text-white w-full border-gray-300 rounded-md focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mt-1"
                v-model="search"
                placeholder="Search..."
            />
        </div>

        <draggable
            v-if="draggable"
            v-model="media"
            @input="(data) => {
                return $emit('input', data)
            }"
            handle=".handle"
            @choose="froalaStore.disableFroala = true"
            @unchoose="froalaStore.disableFroala = false"
        >
            <transition-group
                name="list-complete"
                tag="p"
                :class="[
                    !singleItem
                        ? 'w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'
                        : null,
                ]"
            >
                <media-item
                    @click="handleClick"
                    @shift-click="handleShiftClick"
                    @add-to-gallery="(file) => $emit('add-to-gallery', file)"
                    @remove-from-gallery="(file) => $emit('remove-from-gallery', file)"
                    v-for="(file, index) in media"
                    @delete="handleDelete(index)"
                    :key="`image_${index}`"
                    :file="file || null"
                    :selector="selector"
                    :feedsIntoGallery="feedsIntoGallery"
                    :deletable="deletable"
                    :draggable="draggable"
                    :inGallery="
                        feedsIntoGallery
                            ? gallery
                                  .map((media) => {
                                      return !media ? null : media.id
                                  })
                                  .filter((id) => {
                                      return id != null
                                  })
                                  .includes(file ? file.id : null)
                            : false
                    "
                    :interactive="interactive"
                    :highlight="
                        highlighted
                            .map((img) => {
                                return img ? img.id : null
                            })
                            .filter((id) => {
                                return id != null
                            })
                            .includes(file ? file.id : null)
                    "
                />
            </transition-group>
        </draggable>

        <transition-group
            name="list-complete"
            tag="p"
            v-if="!draggable"
            :class="[
                !singleItem
                    ? 'w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4'
                    : null,
            ]"
        >
            <media-item
                @click="handleClick"
                @shift-click="handleShiftClick"
                @add-to-gallery="(file) => {
                    return $emit('add-to-gallery', file)
                }"
                @remove-from-gallery="(file) => {
                    return $emit('remove-from-gallery', file)
                }"
                v-for="(file, index) in mediaPage"
                @delete="handleDelete(index)"
                :key="`image_${index}`"
                :file="file || null"
                :selector="selector"
                :feedsIntoGallery="feedsIntoGallery"
                :deletable="deletable"
                :inGallery="
                    feedsIntoGallery
                        ? gallery
                              .map((media) => {
                                  return !media ? null : media.id
                              })
                              .filter((id) => {
                                  return id != null
                              })
                              .includes(file ? file.id : null)
                        : false
                "
                :interactive="interactive"
                :highlight="
                    highlighted
                        .map((img) => {
                            return img ? img.id : null
                        })
                        .filter((id) => {
                            return id != null
                        })
                        .includes(file ? file.id : null)
                "
            />
        </transition-group>

        <div class="mx-auto mt-3" v-if="interactive && pageCount > 1">
            <div class="mx-auto w-full flex flex-row items-center justify-between text-black dark:text-white">
                <btn
                    type="button"
                    content="Prev Page"
                    :disabled="this.canDecrement"
                    @click="decrement"
                />

                <span
                    @click="page = index"
                    v-for="(n, index) in pageCount"
                    :key="`index${index}`"
                    class="cursor-pointer"
                    :class="page === index ? 'underline' : null"
                >
                    {{ n }}
                </span>

                <btn
                    type="button"
                    content="Next Page"
                    :disabled="this.canIncrement"
                    @click="increment"
                />
            </div>
        </div>

        <media-modal
            v-if="!selector || !interactive || updatableSelector"
            @deleted="deleteFile"
            @close="fileModalOpen = false"
            :file="selectedFile"
            :active="fileModalOpen"
            :editable="editable"
        ></media-modal>
    </div>
</template>

<script>
import Btn from '../form/Btn'
import media from '../../mixins/media'
import siteLanguage from '../../config/siteLanguage'
import {useFroalaStore} from "../../stores/froala";

export default {
    name: 'MediaLibrary',
    components: {Btn},
    mixins: [media],
    props: {
        value: Array,
        mediaData: Array,
        selector: Boolean,
        updatableSelector: {
            type: Boolean,
            default: false,
        },
        editable: {
            type: Boolean,
            default: true,
        },
        singleItem: {
            type: Boolean,
            default: false,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        deletable: {
            type: Boolean,
            default: false,
            required: false,
        },
        gallery: {
            type: Array,
            default: () => ([]),
        },
        multiple: Boolean,
        feedsIntoGallery: Boolean,
        interactive: {
            type: Boolean,
            default: true,
        },
        language: {
            type: Object,
            default: () => {
                return {iso: siteLanguage.iso.ENGLISH}
            },
        },
    },
    created() {
        if (!this.mediaData) {
            this.getMediaItems()
        }
    },
    data() {
        return {
            fileModalOpen: false,
            selectedFile: null,
            highlighted: this.value || [],
            media: this.mediaData || [],
            page: 0,
            perPage: 30,
            search: '',
            froalaStore: useFroalaStore(),
        }
    },
    methods: {
        handleClick(file) {
            if (!this.selector && !this.gallery) {
                this.openFileModal(file)

                return
            }

            this.handleHighlight(file)
        },
        handleShiftClick(file) {
            this.openFileModal(file)
        },
        handleDelete(index) {
            this.media.splice(index, 1)
            this.$emit('input', this.media)
        },
        openFileModal(file) {
            this.selectedFile = file
            this.fileModalOpen = true
        },
        async deleteFile(file) {
            this.fileModalOpen = false

            await axios
                .delete(route('api.media.delete', file.id))
                .catch((err) => console.error(err.response || err))

            const index = this.media.map((file) => file.id).indexOf(file.id)

            this.media.splice(index, 1)
        },
        async getMediaItems() {
            const response = await axios
                .get(this.indexRoute)
                .catch((err) => {
                    console.error(err.response || err)
                })
            this.media = response.data
        },
        handleHighlight(file) {
            if (
                this.highlighted.map((file) => {
                    return file.id
                }).includes(file.id)
            ) {
                const index = this.highlighted
                    .map((file) => {
                        return file.id
                    })
                    .indexOf(file.id)

                this.highlighted.splice(index, 1)
            } else {
                if (this.multiple) {
                    this.highlighted.push(file)
                } else {
                    this.highlighted.length = 0
                    this.highlighted.push(file)
                }
            }

            this.$emit('input', this.highlighted)
        },
        increment() {
            if (this.canIncrement) {
                return
            }

            this.page++
        },
        decrement() {
            if (this.canDecrement) {
                return
            }

            this.page--
        },
    },
    computed: {
        mediaPage() {
            const start = this.page * this.perPage

            return this.searchedMedia.slice(start, start + this.perPage)
        },
        canIncrement() {
            return (this.page + 1) * this.perPage > this.searchedMedia.length
        },
        canDecrement() {
            return this.page - 1 < 0
        },
        pageCount() {
            return Math.ceil(this.searchedMedia.length / this.perPage)
        },
        searchedMedia() {
            if (!this.search) {
                return this.media
            }

            return this.media.filter((item) => {
                return (
                    item.file_name
                        .toLowerCase()
                        .includes(this.search.toLowerCase())
                    || item.long_description
                        .toLowerCase()
                        .includes(this.search.toLowerCase())
                    || item.alt.toLowerCase().includes(this.search.toLowerCase())
                )
            })
        },
    },
    watch: {
        mediaData(newValue) {
            this.media = newValue
        },
    },
}
</script>
